import type { TransformRef } from '../watermark.types';
import {
  angleBetweenMatrices,
  applyTransform,
  distanceBetweenTranslations2D,
  getDomMatrixAt,
  matrixScale,
} from './matrix-utils';

const updateDivScale = (div: HTMLDivElement, scaled: DOMMatrix) => {
  applyTransform(div, scaled);
  div.style.setProperty('--current-scale', String(matrixScale(scaled)));
  div.style.setProperty('--inverse-current-scale', String(Math.max(0, 1 / matrixScale(scaled))));
};

export const updateTransforms =
  (transforms: React.RefObject<TransformRef>, debugDot: React.RefObject<HTMLDivElement | null>) =>
  (e: React.PointerEvent<HTMLDivElement>) => {
    const {
      selectedDiv,
      selectedElement,
      transformHandle,
      transform,
      pageScale,
      startX,
      startY,
      transformScale,
      centerTransform,
      parentX,
      parentY,
    } = transforms.current;

    if (selectedDiv && selectedElement && transformHandle && transform) {
      const dimensions = selectedElement.settings.dimensions;
      const inverseScale = 1 / pageScale;
      const mouseX = e.pageX - parentX;
      const mouseY = e.pageY - parentY;
      const deltaX = (mouseX - startX) * inverseScale;
      const deltaY = (mouseY - startY) * inverseScale;
      const x = dimensions.x + deltaX;
      const y = dimensions.y + deltaY;

      // new transform for where the mouse has moved to
      const transformHandleMoved = getDomMatrixAt(transformHandle.e + deltaX, transformHandle.f + deltaY);
      debugDot.current?.style.setProperty('transform', transformHandleMoved.toString());
      switch (transforms.current.transformType) {
        case 'box': {
          transform.e = x;
          transform.f = y;
          applyTransform(selectedDiv, transform);
          break;
        }
        case 'bottom-right':
        case 'bottom-left':
        case 'top-right':
        case 'top-left': {
          const newScale = distanceBetweenTranslations2D(transform, transformHandleMoved) / (transformScale ?? 1);
          updateDivScale(selectedDiv, transform.scale(newScale));

          break;
        }
        case 'rotate': {
          if (!centerTransform) return;

          const rotated = transform.rotate(angleBetweenMatrices(centerTransform, transformHandleMoved) + 90);
          applyTransform(selectedDiv, rotated);
          break;
        }
      }
    }
  };
